/**
 * `useApplyDemoChange` - POST /api/demo-sessions/current/change (#2824).
 *
 * The server rebuilds the visitor's demo team with one stored change (or none,
 * to put the team back as it was). The team, the account and the token stay
 * the same. The SPA keeps the picked change on the stored demo session, drops
 * what it read about the old team, and opens the roster on the dates the
 * stored rosters hold (the dates are in the past, so the roster page must be
 * told which ones to show).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { notifyDemoPhone } from "../lib/demoPhone";
import { setDemoVariant } from "../lib/demoSession";
import { type ApiError, apiFetch } from "./client";

export interface DemoChangeResponse {
  team_id: string;
  cycle_id: string;
  scenario: string;
  variant: string | null;
  label: string | null;
  from_date: string;
  to_date: string;
  /** "members" or "stations" for a setup change that marks a row there. */
  page?: string | null;
}

/** The roster page URL for the dates of a changed demo team. */
export function demoRosterPath(
  resp: Pick<DemoChangeResponse, "team_id" | "from_date" | "to_date">,
) {
  return `/teams/${resp.team_id}/roster?from=${resp.from_date}&to=${resp.to_date}&preset=fortnight`;
}

export function useApplyDemoChange(onApplied?: (resp: DemoChangeResponse) => void) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation<DemoChangeResponse, ApiError, string | null>({
    mutationFn: (variant) =>
      apiFetch<DemoChangeResponse>("/api/demo-sessions/current/change", {
        method: "POST",
        body: JSON.stringify({ variant }),
      }),
    onSuccess: (resp) => {
      setDemoVariant(resp.variant);
      // The demo phone reads the rebuilt team.
      notifyDemoPhone();
      // Everything read about the old rows is stale. The public list of demo
      // teams does not change.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "demo" });
      navigate(demoRosterPath(resp));
      onApplied?.(resp);
    },
  });
}
